import * as THREE from 'three';
import { batchStatic } from './batch';
import type { CollisionWorld } from './Collision';
import type { Rect, Vec2 } from './layout';
import { box, M, mesh, worldBox } from './materials';
import { ROOM_SHAPE, roomBounds, roomCenter, type RoomKind, type RoomShape } from './rooms';

const WALL = 0.15;
const STUB = 0.3;

interface Wall {
  upper: THREE.Group;
  nx: number;
  nz: number;
  px: number;
  pz: number;
}

/** What a room's furnishing code gets to work with (local coordinates; the door is on +z). */
export interface RoomKit {
  readonly shape: RoomShape;
  /** Something that moves, glows on its own or gets outlined: kept as its own object. */
  place<T extends THREE.Object3D>(obj: T, x: number, z: number, rot?: number): T;
  /** Furniture that never changes: merged into a few meshes once the room's built. */
  add<T extends THREE.Object3D>(obj: T, x: number, z: number, rot?: number): T;
  /** Collision box (w along x, d along z), local coordinates. */
  solid(x: number, z: number, w: number, d: number): void;
  circle(x: number, z: number, r: number): void;
  /** The parts of the walls that cut away (hang pictures and signs on these). */
  readonly walls: { back: THREE.Group; front: THREE.Group; west: THREE.Group; east: THREE.Group };
  /** Only shown when the camera's inside the room. */
  readonly ceiling: THREE.Group;
  animate(fn: (time: number, dt: number) => void): void;
}

export interface RoomFinish {
  wall: THREE.Material;
  floor: THREE.Material;
  ceiling: THREE.Material;
}

/**
 * A room anyone can walk into: a shop, a neighbor's place. Same idea as the bar: walls
 * that cut away when the camera's outside them, a ceiling you only see from inside,
 * and furniture placed by whoever builds it.
 */
export class PublicRoom {
  readonly group = new THREE.Group();
  readonly center: Vec2;
  readonly shape: RoomShape;
  private readonly walls: Wall[] = [];
  private readonly ceiling = new THREE.Group();
  private readonly animators: Array<(time: number, dt: number) => void> = [];

  constructor(
    parent: THREE.Object3D,
    collision: CollisionWorld,
    readonly slot: number,
    readonly kind: RoomKind,
    finish: RoomFinish,
    furnish: (kit: RoomKit) => void,
  ) {
    const shape = ROOM_SHAPE[kind];
    this.shape = shape;
    const { length: L, depth: D, height: H, doorX } = shape;
    const c = roomCenter(slot);
    this.center = c;
    this.group.position.set(c.x, 0, c.z);
    this.group.add(
      mesh(worldBox(L + 2 * WALL, 0.1, D + 2 * WALL, 1.4), finish.floor, 0, -0.05, 0, { cast: false }),
    );
    const addWall = (len: number, cx: number, cz: number, alongX: boolean, nx: number, nz: number): void => {
      const w = alongX ? len : WALL;
      const d = alongX ? WALL : len;
      this.group.add(mesh(worldBox(w, STUB, d, 1.2), finish.wall, cx, STUB / 2, cz, { cast: false }));
      const upper = new THREE.Group();
      upper.add(mesh(worldBox(w, H - STUB, d, 1.2), finish.wall, cx, STUB + (H - STUB) / 2, cz));
      this.group.add(upper);
      this.walls.push({ upper, nx, nz, px: c.x + cx - nx * (WALL / 2), pz: c.z + cz - nz * (WALL / 2) });
      collision.addRect(c.x + cx, c.z + cz, w, d);
    };
    addWall(L + 2 * WALL, 0, -D / 2 - WALL / 2, true, 0, -1);
    addWall(L + 2 * WALL, 0, D / 2 + WALL / 2, true, 0, 1);
    addWall(D, -L / 2 - WALL / 2, 0, false, -1, 0);
    addWall(D, L / 2 + WALL / 2, 0, false, 1, 0);
    const [back, front, west, east] = this.walls.map((w) => w.upper) as [
      THREE.Group,
      THREE.Group,
      THREE.Group,
      THREE.Group,
    ];

    // The way out: a dark doorway with a glass door, on the inside of the front wall.
    const door = new THREE.Group();
    door.add(box(1.5, 2.4, 0.05, M.black, 0, 1.2, 0));
    door.add(box(1.3, 2.2, 0.03, M.darkGlass, 0, 1.15, -0.03, { cast: false }));
    door.add(box(0.06, 0.4, 0.06, M.chrome, 0.5, 1.1, -0.08, { cast: false }));
    door.position.set(doorX, 0, D / 2 - 0.01);
    front.add(door);

    const ceiling = mesh(new THREE.PlaneGeometry(L + 0.3, D + 0.3), finish.ceiling, 0, H, 0, { cast: false });
    ceiling.rotation.x = Math.PI / 2;
    this.ceiling.add(ceiling);
    this.ceiling.visible = false;
    this.group.add(this.ceiling);

    const statics = new THREE.Group();
    const put =
      (into: THREE.Object3D) =>
      <T extends THREE.Object3D>(obj: T, x: number, z: number, rot = 0): T => {
        obj.position.x = x;
        obj.position.z = z;
        obj.rotation.y = rot;
        into.add(obj);
        return obj;
      };
    const kit: RoomKit = {
      shape,
      place: put(this.group),
      add: put(statics),
      solid: (x, z, w, d) => void collision.addRect(c.x + x, c.z + z, w, d),
      circle: (x, z, r) => void collision.addCircle(c.x + x, c.z + z, r),
      walls: { back, front, west, east },
      ceiling: this.ceiling,
      animate: (fn) => this.animators.push(fn),
    };
    furnish(kit);
    // Batched while detached (at the origin), so merged geometry stays in room-local space.
    batchStatic(statics);
    this.group.add(statics);
    parent.add(this.group);
  }

  get bounds(): Rect {
    return roomBounds(this.slot, this.kind);
  }

  world(p: Vec2): Vec2 {
    return { x: this.center.x + p.x, z: this.center.z + p.z };
  }

  get entry(): Vec2 {
    return this.world({ x: this.shape.doorX, z: this.shape.depth / 2 - 0.9 });
  }

  get exit(): Vec2 {
    return this.world({ x: this.shape.doorX, z: this.shape.depth / 2 - 0.4 });
  }

  updateCutaway(camera: THREE.Vector3): void {
    for (const w of this.walls) {
      const out = (camera.x - w.px) * w.nx + (camera.z - w.pz) * w.nz;
      w.upper.visible = out < 0.2;
    }
    const b = this.bounds;
    this.ceiling.visible =
      camera.y < this.shape.height &&
      camera.x > b.minX &&
      camera.x < b.maxX &&
      camera.z > b.minZ &&
      camera.z < b.maxZ;
  }

  update(time: number, dt: number): void {
    for (const fn of this.animators) fn(time, dt);
  }
}
